import { type CSSProperties, type HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';

const statusBadgeVariants = cva(
  'inline-flex items-center rounded-full font-medium',
  {
    variants: {
      variant: {
        success:   '',
        warning:   '',
        danger:    '',
        info:      '',
        neutral:   '',
        pending:   '',
        progress:  '',
        /* 카페24 정합 한국 쇼핑몰 상태 */
        shipping:  '',
        delivered: '',
        cancelled: '',
        refunded:  '',
        paid:      '',
        failed:    '',
      },
      size: {
        sm: 'px-2 py-0.5 text-[11px]',
        md: 'px-2.5 py-0.5 text-xs',
      },
    },
    defaultVariants: { variant: 'info', size: 'md' },
  },
);

type StatusBadgeVariant = NonNullable<VariantProps<typeof statusBadgeVariants>['variant']>;

const BADGE_STYLES: Record<StatusBadgeVariant, CSSProperties> = {
  success:   { background: 'var(--status-success-bg)',   color: 'var(--status-success-fg)' },
  warning:   { background: 'var(--status-warn-bg)',      color: 'var(--status-warn-fg)' },
  danger:    { background: 'var(--status-danger-bg)',    color: 'var(--status-danger-fg)' },
  info:      { background: 'var(--status-info-bg)',      color: 'var(--status-info-fg)' },
  neutral:   { background: 'var(--status-neutral-bg)',   color: 'var(--status-neutral-fg)' },
  pending:   { background: 'var(--status-pending-bg)',   color: 'var(--status-pending-fg)' },
  progress:  { background: 'var(--status-progress-bg)',  color: 'var(--status-progress-fg)' },
  shipping:  { background: 'var(--status-shipping-bg)',  color: 'var(--status-shipping-fg)' },
  delivered: { background: 'var(--status-delivered-bg)', color: 'var(--status-delivered-fg)' },
  cancelled: { background: 'var(--status-cancelled-bg)', color: 'var(--status-cancelled-fg)' },
  refunded:  { background: 'var(--status-refunded-bg)',  color: 'var(--status-refunded-fg)' },
  paid:      { background: 'var(--status-paid-bg)',      color: 'var(--status-paid-fg)' },
  failed:    { background: 'var(--status-failed-bg)',    color: 'var(--status-failed-fg)' },
};

export interface StatusBadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof statusBadgeVariants> {
  label: string;
}

export const StatusBadge = ({ label, variant, size, className, style, ...props }: StatusBadgeProps) => {
  const v = (variant ?? 'info') as StatusBadgeVariant;
  const isDanger = v === 'danger' || v === 'failed';
  const dangerProps = isDanger
    ? {
        animate: { opacity: [1, 0.65, 1] },
        transition: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' as const },
      }
    : {};
  return (
    <motion.span
      className={cn(statusBadgeVariants({ variant, size }), className)}
      style={{ ...BADGE_STYLES[v], ...style } as object}
      {...dangerProps}
      {...(props as object)}
    >
      {label}
    </motion.span>
  );
};
